import { Injectable } from '@angular/core';
import { ComponentStore, tapResponse } from '@ngrx/component-store';
import { Observable, exhaustMap } from 'rxjs';
import { BannerCarouselApiModel } from '@woolworthsnz/trader-api';
import { ApiService, AppSettingsService, LoggingService } from '@woolworthsnz/styleguide';

export enum CarouselRotationStatus {
	STARTED = 'started',
	STOPPED = 'stopped',
	PAUSED = 'paused',
}

export interface HeroCarouselState {
	currentIndex: number;
	toLeft: boolean;
	hasError: boolean;
	loading: boolean;
	heroItems: BannerCarouselApiModel[];
	rotationStatus: CarouselRotationStatus;
}

export const defaultState: HeroCarouselState = {
	currentIndex: 0,
	toLeft: true,
	hasError: false,
	loading: false,
	heroItems: [],
	rotationStatus: CarouselRotationStatus.STARTED,
};

@Injectable()
export class HeroCarouselStore extends ComponentStore<HeroCarouselState> {
	/** Selectors */
	selectCurrentIndex$: Observable<number> = this.select((state) => state.currentIndex);
	selectToLeft$: Observable<boolean> = this.select((state) => state.toLeft);
	selectHeroItems$: Observable<BannerCarouselApiModel[]> = this.select((state) => state.heroItems);
	selectError$: Observable<boolean> = this.select((state) => state.hasError);
	selectIsLoading$ = this.select((state) => state.loading);
	selectRotationStatus$: Observable<CarouselRotationStatus> = this.select((state) => state.rotationStatus);

	/** Updaters */

	public readonly updateToLeft = this.updater(
		(state: HeroCarouselState, toLeft: boolean): HeroCarouselState => ({
			...state,
			toLeft,
		})
	);

	public readonly updateCurrentIndex = this.updater(
		(state: HeroCarouselState, currentIndex: number): HeroCarouselState => ({
			...state,
			currentIndex: Number.isNaN(currentIndex) ? 0 : currentIndex,
		})
	);

	public readonly updateHasError = this.updater(
		(state, hasError: boolean): HeroCarouselState => ({
			...state,
			hasError,
		})
	);

	public readonly updateIsLoading = this.updater(
		(state: HeroCarouselState, loading: boolean): HeroCarouselState => ({
			...state,
			loading,
		})
	);

	public readonly updateHeroItems = this.updater(
		(state, heroItems: BannerCarouselApiModel[]): HeroCarouselState => ({
			...state,
			heroItems,
		})
	);

	public readonly updateRotationStatus = this.updater(
		(state: HeroCarouselState, rotationStatus: CarouselRotationStatus): HeroCarouselState => ({
			...state,
			rotationStatus,
		})
	);

	/** Effects */
	public readonly loadHeroItems = this.effect((trigger$) =>
		trigger$.pipe(
			exhaustMap(() =>
				this.apiService.get(this.appSettingsService.getEndpoint('homepageBanners')).pipe(
					tapResponse(
						(response) => {
							this.updateHeroItems(response.banners);
							this.updateIsLoading(false);
							this.updateHasError(false);
						},
						(error) => {
							this.updateHasError(true);
							this.updateIsLoading(false);
							this.loggingService.trackException({ error: new Error(error as string) });
						}
					)
				)
			)
		)
	);

	constructor(
		private apiService: ApiService,
		private appSettingsService: AppSettingsService,
		private loggingService: LoggingService
	) {
		super(defaultState);
	}
}
